import { ActionItem, UnpaidWages } from '../../../interfaces/dashboard.interface';
import { IPayrollPendingApproval } from '../../../interfaces/payroll.interface';
import { PayrollService } from '../../../services/payroll.service';
import { formatMoney } from '../../../utils/format';
import { formatPeriodLabel } from '../charts/axisPadding';

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const ym = (year: number, month: number) => year * 12 + month;

/**
 * Payroll lines for "Needs action" (board DA-14): one line per month, newest first, each with its state.
 * - Awaiting approval: a finalized month the approver has not approved yet (or that changed after approval).
 *   When a later month is already approved, the line says it is the older run.
 * - Approved, unpaid: a finalized month that is no longer awaiting approval but still has an unpaid remainder,
 *   with its legal pay-by date (BD Labour Act s.123).
 * A maker of a run is not offered that run's approval (maker-checker), but the run still counts as awaiting so it is
 * not reported as "approved, unpaid". Ids keep the `payroll.awaiting.` prefix so hide and help keys cover them.
 * `unpaid` is null when the caller cannot see it; the lines then fall back to approvals only.
 */
export function buildPayrollActionItems(awaiting: IPayrollPendingApproval[], unpaid: UnpaidWages | null): ActionItem[] {
    const waitingKeys = new Set(awaiting.map(m => ym(m.year, m.month)));
    const approvedUnpaid = (unpaid?.items ?? []).filter(i => !waitingKeys.has(ym(i.year, i.month)));
    const newestApproved = approvedUnpaid.reduce((n, i) => Math.max(n, ym(i.year, i.month)), 0);

    const awaitingItems = awaiting.filter(m => !m.callerIsMaker).map(m => {
        const older = newestApproved > ym(m.year, m.month);
        return {
            at: ym(m.year, m.month),
            item: {
                id: `payroll.awaiting.${m.year}-${m.month}`,
                severity: 'WARNING' as const,
                module: 'payroll',
                title: `Payroll awaiting approval: ${m.periodLabel}`,
                detail: `${formatMoney(m.netTotal, 'BDT')} net for ${plural(m.headcount, 'worker')}`
                    + (m.state === 'CHANGED' ? ', changed after it was approved' : '')
                    + (older ? '; older run, a later month is already approved' : ''),
                count: 0,
                actionLabel: 'Review and approve',
                link: `/payroll/run?year=${m.year}&month=${m.month}`,
            } as ActionItem,
        };
    });
    const unpaidItems = approvedUnpaid.map(i => ({
        at: ym(i.year, i.month),
        item: {
            id: `payroll.awaiting.${i.year}-${i.month}.unpaid`,
            severity: i.overdue ? ('CRITICAL' as const) : ('WARNING' as const),
            module: 'payroll',
            title: `Payroll approved, unpaid: ${formatPeriodLabel(i.period)}`,
            detail: `${formatMoney(i.remaining, 'BDT')} still to pay, legal deadline ${i.legalDeadline}`
                + (i.overdue ? ' (passed)' : ''),
            count: 0,
            actionLabel: 'Open payroll run',
            link: `/payroll/run?year=${i.year}&month=${i.month}`,
        } as ActionItem,
    }));
    return [...awaitingItems, ...unpaidItems].sort((a, b) => b.at - a.at).map(x => x.item);
}

/** Loads both inputs; the unpaid figure is optional (a 403 or a failure leaves it null). */
export async function loadPayrollActionItems(institutionId: string): Promise<ActionItem[]> {
    const [awaiting, unpaid] = await Promise.all([
        PayrollService.getAwaitingApproval(institutionId),
        Promise.resolve().then(() => PayrollService.getUnpaidWages(institutionId)).catch(() => null),
    ]);
    return buildPayrollActionItems(Array.isArray(awaiting) ? awaiting : [], unpaid ?? null);
}
